import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import Overlay from "../../Componentes/Overlay";
import SideMenu from "../../Componentes/SideMenu";
import SideMenuBtn from "../../Componentes/SideMenuBtn";
import SortableTable from "../../Componentes/Utils/SortableTable";
import { useModal } from "../../contexts/ModalContext";
import { useToast } from "../../contexts/ToastContext";
import useApi from "../../hooks/useApi";
import { formatCPF } from "../../Funcoes/FormatCPF";
import { formatarData } from "../../Funcoes/FormatarData";
import ModalNovaSolicitacao from "./ModalNovaSolicitacao";
import ModalEnviarPassagens, { MODAL_ENVIAR_PASSAGENS } from "./ModalEnviarPassagens";

const PageStretch = styled.div`
    flex: 1;
    min-height: 0;
    width: 100%;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
`;

const TableFlexFill = styled.div`
    flex: 1;
    min-height: 0;
    width: 100%;
    display: flex;
    flex-direction: column;

    & > div {
        flex: 1;
        min-height: 0;
    }
`;

const StatusBadge = styled.span`
    display: inline-block;
    padding: 3px 8px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
    background-color: ${(props) => {
        if (props.status === "PENDENTE") return "#fef3c7";
        if (props.status === "INSERIR VALORES") return "#dbeafe";
        if (props.status === "APROVADO") return "#dcfce7";
        if (props.status === "CANCELADO") return "#fee2e2";
        return "#e2e8f0";
    }};
    color: ${(props) => {
        if (props.status === "PENDENTE") return "#92400e";
        if (props.status === "INSERIR VALORES") return "#1e40af";
        if (props.status === "APROVADO") return "#166534";
        if (props.status === "CANCELADO") return "#991b1b";
        return "#334155";
    }};
`;

const Checkbox = styled.input.attrs({ type: "checkbox" })`
    width: 15px;
    height: 15px;
    cursor: pointer;
    accent-color: #263B4B;
`;

const SelectionBar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 12px;
    margin-bottom: 10px;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.12);
    color: #fff;
    flex-shrink: 0;
`;

const SelectionInfo = styled.span`
    font-size: 13px;
    font-weight: 600;
`;

const BotaoEnviar = styled.button`
    padding: 8px 14px;
    border: none;
    border-radius: 8px;
    background-color: #00A651;
    color: #fff;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;

    &:hover {
        background-color: #008f45;
    }
`;

const formatarLocal = (municipio, estado) => {
    if (!municipio) return "—";
    return estado ? `${municipio} / ${estado}` : municipio;
};

const formatarDataNasc = (data) => {
    if (!data) return "—";
    try {
        return formatarData(String(data));
    } catch {
        return String(data);
    }
};

const formatarHorario = (horario) => {
    if (!horario) return "—";
    try {
        return formatarData(String(horario).slice(0, 19), true);
    } catch {
        return String(horario);
    }
};

const BodyContent = () => {

    const api = useApi();
    const { isModalOpen, closeModal, openModal } = useModal();
    const { notifyError } = useToast();

    const [loading, setLoading] = useState(true);
    const [solicitacoes, setSolicitacoes] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);

    const fetchSolicitacoes = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/Painel/API/SolicitacaoPassagem/List/");
            if (!data?.success) {
                notifyError(data?.message || "Erro ao carregar solicitações.");
                setSolicitacoes([]);
                setSelectedIds([]);
                return;
            }
            const rows = Array.isArray(data.data) ? data.data : [];
            setSolicitacoes(rows);
            setSelectedIds((atual) =>
                atual.filter((id) => rows.some((row) => row.id === id))
            );
        } catch (err) {
            console.error(err);
            notifyError(err?.response?.data?.message || "Erro ao carregar solicitações.");
            setSolicitacoes([]);
            setSelectedIds([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSolicitacoes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const openNovaSolicitacao = () => {
        closeModal();
        openModal("novaSolicitacaoPassagem");
    };

    const openEnviarPassagens = () => {
        if (selectedIds.length === 0) {
            notifyError("Selecione ao menos uma solicitação.");
            return;
        }
        openModal(MODAL_ENVIAR_PASSAGENS);
    };

    const handleEnviado = async () => {
        setSelectedIds([]);
        await fetchSolicitacoes();
    };

    const todosSelecionados = solicitacoes.length > 0
        && solicitacoes.every((row) => selectedIds.includes(row.id));

    const toggleTodos = () => {
        if (todosSelecionados) {
            setSelectedIds([]);
            return;
        }
        setSelectedIds(solicitacoes.map((row) => row.id));
    };

    const toggleLinha = (id) => {
        setSelectedIds((atual) =>
            atual.includes(id)
                ? atual.filter((item) => item !== id)
                : [...atual, id]
        );
    };

    const columns = useMemo(() => [
        {
            id: "select",
            header: (
                <Checkbox
                    checked={todosSelecionados}
                    onChange={toggleTodos}
                    onClick={(e) => e.stopPropagation()}
                    title={todosSelecionados ? "Desmarcar todos" : "Selecionar todos"}
                />
            ),
            accessor: "id",
            sortable: false,
            align: "center",
            width: "42px",
            render: (_value, row) => (
                <Checkbox
                    checked={selectedIds.includes(row.id)}
                    onChange={() => toggleLinha(row.id)}
                    onClick={(e) => e.stopPropagation()}
                />
            ),
        },
        {
            id: "matricula",
            header: "MATRÍCULA",
            accessor: "matricula",
            sortable: true,
            align: "center",
            render: (value) => value || "—",
        },
        {
            id: "nome",
            header: "NOME",
            accessor: "nome",
            sortable: true,
            align: "left",
        },
        {
            id: "data_nasc",
            header: "DATA DE NASC",
            accessor: "data_nasc",
            sortable: true,
            align: "center",
            render: (value) => formatarDataNasc(value),
        },
        {
            id: "cpf",
            header: "CPF",
            accessor: "cpf",
            sortable: true,
            align: "center",
            render: (value) => (value ? formatCPF(String(value)) : "—"),
        },
        {
            id: "rg",
            header: "RG",
            accessor: "rg",
            sortable: true,
            align: "center",
            render: (value) => value || "—",
        },
        {
            id: "centro_custo",
            header: "CENTRO DE CUSTO",
            accessor: "centro_custo",
            sortable: true,
            align: "left",
            render: (value) => value || "—",
        },
        {
            id: "de",
            header: "DE",
            accessor: (row) => formatarLocal(row.de_municip, row.de_estado),
            sortable: true,
            align: "center",
            render: (_value, row) => formatarLocal(row.de_municip, row.de_estado),
        },
        {
            id: "para",
            header: "PARA",
            accessor: (row) => formatarLocal(row.para_municip, row.para_estado),
            sortable: true,
            align: "center",
            render: (_value, row) => formatarLocal(row.para_municip, row.para_estado),
        },
        {
            id: "horario",
            header: "HORÁRIO",
            accessor: "horario",
            sortable: true,
            align: "center",
            render: (value) => formatarHorario(value),
        },
        {
            id: "status",
            header: "STATUS",
            accessor: "status",
            sortable: true,
            align: "center",
            render: (value) => (
                <StatusBadge status={value}>
                    {value || "—"}
                </StatusBadge>
            ),
        },
    ], [selectedIds, todosSelecionados, solicitacoes]);

    return (
        <>
            <PageStretch>
                {selectedIds.length > 0 && (
                    <SelectionBar>
                        <SelectionInfo>
                            {selectedIds.length} solicitação(ões) selecionada(s)
                        </SelectionInfo>
                        <BotaoEnviar type="button" onClick={openEnviarPassagens}>
                            ENVIAR POR E-MAIL
                        </BotaoEnviar>
                    </SelectionBar>
                )}
                <TableFlexFill>
                    <SortableTable
                        columns={columns}
                        data={solicitacoes}
                        getRowId={(row) => row.id}
                        isLoading={loading}
                        loadingText="Carregando solicitações..."
                        emptyText="Nenhuma solicitação de passagem encontrada."
                        initialSort={{ key: "horario", direction: "desc" }}
                        onRowClick={(row) => toggleLinha(row.id)}
                        rowCursor="pointer"
                        getRowProps={(row) => ({
                            style: selectedIds.includes(row.id)
                                ? { backgroundColor: "#dbeafe" }
                                : undefined
                        })}
                    />
                </TableFlexFill>
            </PageStretch>

            {isModalOpen("solicPassagemSidebar") && (
                <Overlay state={true} changeContent={closeModal}>
                    <SideMenu>
                        <SideMenuBtn
                            button="NOVA SOLICITAÇÃO"
                            clickFunc={openNovaSolicitacao}
                        />
                        {selectedIds.length > 0 && (
                            <SideMenuBtn
                                button="ENVIAR POR E-MAIL"
                                clickFunc={() => {
                                    closeModal();
                                    openEnviarPassagens();
                                }}
                            />
                        )}
                    </SideMenu>
                </Overlay>
            )}

            {isModalOpen("novaSolicitacaoPassagem") && (
                <ModalNovaSolicitacao fetchSuccess={fetchSolicitacoes} />
            )}

            {isModalOpen(MODAL_ENVIAR_PASSAGENS) && (
                <ModalEnviarPassagens
                    ids={selectedIds}
                    onSuccess={handleEnviado}
                />
            )}
        </>
    )

}

export default BodyContent;
